import produce, { Draft } from "immer";
import { v4 } from "uuid";

import { DupInsuranceProductType, PersonCoverage } from "../../interfaces/dup";
import { Action, DupState, UpdateInsuredAmountPayload } from "./DupFlow.interfaces";

export const initialState: DupState = {
  insuredPersons: [{ isDefault: true, isChild: false, personId: v4() }],
  paymentTerm: "Monthly",
  defaultCoverage: {
    effectiveDate: undefined,
    paymentPeriod: undefined,
    insuranceType: undefined,
    insuredAmount: undefined,
  },
};

const updateCoverageInsuranceAmount = (
  draft: Draft<DupState>,
  action: { payload: { index: number; insuredAmount: number } }
) => {
  const insuredPerson = draft.insuredPersons[action.payload.index];
  if (insuredPerson && insuredPerson.personCoverage) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    draft.insuredPersons[action.payload.index].personCoverage!.insuredAmount = action.payload.insuredAmount;
    draft.insuredPersons[action.payload.index].isApplied = true;
  }
};

const updateCoverageInsuranceType = (draft: Draft<DupState>, index: number, insuranceType: DupInsuranceProductType) => {
  const insuredPerson = draft.insuredPersons[index];
  if (insuredPerson && draft.insuredPersons[index].personCoverage) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    draft.insuredPersons[index].personCoverage!.insuranceType = insuranceType;
    draft.insuredPersons[index].isApplied = true;
  }
};

const updateInsuredPersonCoverage = (
  draft: Draft<DupState>,
  index: number,
  coverage: PersonCoverage,
  isApplied: boolean
) => {
  const insuredPerson = draft.insuredPersons[index];
  if (insuredPerson) {
    draft.insuredPersons[index].personCoverage = coverage;
    draft.insuredPersons[index].isApplied = isApplied;
  }
};

const updateInsuredAmount = (draft: Draft<DupState>, payload: UpdateInsuredAmountPayload) => {
  draft.defaultCoverage.insuredAmount = payload.insuredAmount;
  if (payload.hasInsuredAmount) {
    draft.defaultCoverage.hasInsuredAmount = true;
  }
  if (draft.insuredPersons.length > 1) {
    draft.insuredPersons.map((insuredPerson) => {
      if (!insuredPerson.isDefault && !insuredPerson.isApplied) {
        insuredPerson.personCoverage = {
          insuranceType:
            insuredPerson.personCoverage?.insuranceType ?? draft.defaultCoverage.insuranceType ?? "Unknown",
          insuredAmount: payload.insuredAmount,
        };
      }
      return insuredPerson;
    });
  }
};

const updateInsuranceType = (draft: Draft<DupState>, insuranceType: DupInsuranceProductType) => {
  draft.defaultCoverage.insuranceType = insuranceType;

  if (draft.insuredPersons.length > 1) {
    draft.insuredPersons.map((insuredPerson) => {
      if (!insuredPerson.isDefault && !insuredPerson.isApplied) {
        insuredPerson.personCoverage = {
          insuredAmount: insuredPerson.personCoverage?.insuredAmount ?? draft.defaultCoverage.insuredAmount ?? 0,
          insuranceType: insuranceType,
        };
      }
      return insuredPerson;
    });
  }
};

const createInsuredPerson = (
  state: DupState,
  action: { type: "CREATE_INSURED_PERSON"; payload: { isChild: boolean } },
  draft: Draft<DupState>
) => {
  const person = {
    isDefault: false,
    isApplied: false,
    personCoverage: {
      insuranceType: state.defaultCoverage.insuranceType ?? "Unknown",
      insuredAmount: state.defaultCoverage.insuredAmount ?? 0,
    },
    isChild: action.payload.isChild,
    personId: v4(),
  };
  if (person.isChild) {
    draft.insuredPersons.push(person);
  } else {
    draft.insuredPersons.splice(1, 0, person);
  }

  if (draft.insuredPersons.length > 2 && draft.defaultCoverage?.insuranceType === "PurchaseMoneyProduct") {
    draft.defaultCoverage.insuranceType = undefined;
  }
};

export const dupFlowStateReducer = (state: DupState, action: Action): DupState => {
  switch (action.type) {
    case "REHYDRATE":
      return action.payload.data;
    case "UPDATE_INSURED_DATA":
      return produce(state, (draft) => {
        const insuredPerson = draft.insuredPersons[action.payload.index];
        if (insuredPerson) {
          draft.insuredPersons[action.payload.index] = { ...insuredPerson, ...action.payload.data };
        }
      });
    case "CREATE_INSURED_PERSON":
      return produce(state, (draft) => {
        createInsuredPerson(state, action, draft);
      });
    case "DELETE_INSURED_PERSON":
      return produce(state, (draft) => {
        draft.insuredPersons.splice(action.payload.index, 1);
      });
    case "UPDATE_INSURANCE_TYPE":
      return produce(state, (draft) => {
        updateInsuranceType(draft, action.payload.insuranceType);
      });
    case "UPDATE_INSURED_AMOUNT":
      return produce(state, (draft) => {
        updateInsuredAmount(draft, action.payload);
      });
    case "UPDATE_HAS_INSURED_AMOUNT":
      return produce(state, (draft) => {
        draft.defaultCoverage.hasInsuredAmount = true;
      });
    case "UPDATE_EFFECTIVE_DATE":
      return produce(state, (draft) => {
        draft.defaultCoverage.effectiveDate = action.payload.date;
      });
    case "UPDATE_PAYMENT_PERIOD":
      return produce(state, (draft) => {
        draft.defaultCoverage.paymentPeriod = action.payload.paymentPeriod;
      });
    case "UPDATE_INSURED_PERSON_COVERAGE":
      return produce(state, (draft) => {
        updateInsuredPersonCoverage(draft, action.payload.index, action.payload.coverage, action.payload.isApplied);
      });
    case "UPDATE_INSURED_PERSON_COVERAGE_INSURANCE_TYPE":
      return produce(state, (draft) => {
        updateCoverageInsuranceType(draft, action.payload.index, action.payload.insuranceType);
      });
    case "UPDATE_INSURED_PERSON_COVERAGE_INSURED_AMOUNT":
      return produce(state, (draft) => {
        updateCoverageInsuranceAmount(draft, action);
      });
    case "UPDATE_PAYMENT_TERM":
      return produce(state, (draft) => {
        draft.paymentTerm = action.payload.paymentTerm;
      });
  }
};
